import React from 'react';
import { bool, string } from 'prop-types';
import {
  Bar, BarChart, Cell, ResponsiveContainer, XAxis, YAxis
} from 'recharts';
import tinygradient from 'tinygradient';

import VariableSummaryLegend from '../VariableSummaryLegend';
import { variableStatsType } from '../../../constants/types';
import variables from '../../../styles/variables.json';
import styles from './absoluteVariableSummary.module.css';
import { getTranslation } from '../../../i18n';

const {
  black, blue, darkBlueActive, darkSilver
} = variables;

const blueGradient = tinygradient(blue, darkBlueActive);
const CONTAINER_SIZE = { height: 180 };

const getData = (stats) => stats.absoluteCategories.map(
  (category, i, arr) => ({
    label: `${category.value} - ${arr[i + 1] ? arr[i + 1].value - 1 : stats.maxTotal}`,
    value: stats.distribution[`${category.value}`]
  })
);

const compareAbsoluteCategoryValues = (a1, a2) => {
  if (a1.value < a2.value) {
    return -1;
  }
  if (a1.value > a2.value) {
    return 1;
  }
  return 0;
};

const isLastIndexOfArray = (index, array) => index === array.length - 1;

const getColors = (numSlots) => blueGradient.rgb(numSlots < 2 ? 2 : numSlots);

const getAbsoluteCategoryLegend = (maxTotal, lang, numSlots) =>
  (absoluteCategory, index, array) => {
    if (maxTotal === 0) {
      return null;
    }

    const colors = getColors(numSlots);
    let valueString;

    const currentValue = absoluteCategory.value;
    const description = getTranslation(absoluteCategory.description, lang);

    if (isLastIndexOfArray(index, array)) {
      valueString = `${currentValue} - ${maxTotal}`;
    } else {
      valueString = `${currentValue} - ${array[index + 1].value - 1}`;
    }

    const valueColor = { backgroundColor: colors[index] };

    return (
      <div key={index}>
        <span className={styles.categoryColor} style={valueColor} />
        <span>{`${valueString} ${description}`}</span>
      </div>
    );
  };

const AbsoluteVariableSummary = (props) => {
  const {
    userTotal,
    averageTotal,
    feedback,
    absoluteCategories,
    maxTotal,
    minTotal,
    maxSum,
    minSum,
    standardDeviation
  } = props.stats;

  const colors = getColors(absoluteCategories.length);
  const toPercentage = (val) => `${(val * 100).toFixed(0)}%`;
  absoluteCategories.sort(compareAbsoluteCategoryValues);
  const chartData = getData(props.stats);
  chartData[0].range = [minTotal, maxTotal];
  const numSlots = absoluteCategories.length;
  const yDomain = [0, 1];
  const yTicks = Array.from({ length: 13 }, (v, k) => k / 10);

  return (
    <div>
      <figure className={styles.chart} aria-label={props.label}>
        <div aria-hidden>
          <ResponsiveContainer height={CONTAINER_SIZE.height} debounce={300}>
            <BarChart data={chartData}>
              <XAxis
                stroke={darkSilver}
                interval={0}
                tickLine={false}
                domain={chartData[0].range}
                dataKey="label"
              />
              <YAxis
                stroke={darkSilver}
                domain={yDomain}
                ticks={yTicks}
                tickFormatter={toPercentage}
                tickLine={false}
                interval={4}
              />
              <Bar dataKey="value" label={{ fill: black, position: 'top', formatter: toPercentage }}>
                {
                  chartData.map((k, index) => (
                    <Cell key={k} fill={`${colors[index]}`} />
                  ))
                }
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <figcaption className={styles.chartDescription}>
          {absoluteCategories.map(getAbsoluteCategoryLegend(maxTotal, props.lang, numSlots))}
          <div className={styles.summary}>
            <VariableSummaryLegend
              userValue={userTotal}
              userKey="surveySummary.userTotal"
              groupValue={averageTotal}
              groupKey="surveySummary.groupAverage"
              feedback={feedback}
              showAverageIndicator={false}
              showStandardDeviation={props.showStandardDeviation}
              standardDeviation={standardDeviation}
              minAverage={minSum}
              maxAverage={maxSum}
            />
          </div>
        </figcaption>
      </figure>
    </div>
  );
};

AbsoluteVariableSummary.propTypes = {
  lang: string.isRequired,
  stats: variableStatsType.isRequired,
  showStandardDeviation: bool,
  label: string.isRequired
};

AbsoluteVariableSummary.defaultProps = {
  showStandardDeviation: false
};

export default AbsoluteVariableSummary;
